import React, { useEffect, useMemo } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import {
  getInternshipInfoThunk,
  getInternshipPeopleListThunk,
} from '../../../redux/Internship/Internship.thunk';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import {
  getInternshipPeopleList,
  getInternshipSelected,
  resetInternshipSelectedAction,
  resetInternshipSelectedPeopleAction,
} from '../../../redux/Internship/Internship.slice';
import styled from 'styled-components';
import BaseModalComponent, { StyledBaseModal } from '../BaseModal.component';
import { StyledSpinnerView } from '../../Spinner.component';
import InternshipInfoBlockComponent from '../../Blocks/InternshipInfoBlock.component';
import ButtonComponent from '../../Button.component';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUrlBasedOnCurrentPath } from '../Candidate/utils';
import { RoutesConstant } from '../../../constants/routes.constant';
import { CANDIDATES_GROUPS } from '../../../types/candidates.types';
import { useModalManager } from '../../../hooks/ModalManager.hook';

interface InternshipInfoModalProps
  extends React.AllHTMLAttributes<HTMLDivElement> {
  internshipId: number;
}

const StyledInternshipInfoModal = styled.div`
  ${StyledBaseModal} {
    width: 430px;
  }

  ${StyledSpinnerView} {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: ${({ theme }) => theme.colors.CARD};
    border-radius: 8px;
  }
`;

const StyledFooterButtonContainer = styled.div`
  flex: 1 0 50%;

`;

const InternshipInfoModal = ({ internshipId }: InternshipInfoModalProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { item } = useAppSelector(getInternshipSelected);
  const { items: peopleList } = useAppSelector(getInternshipPeopleList);

  const { closeModal } = useModalManager();
  const navigate = useNavigate();

  useEffect(() => {
    if (!internshipId) return;
    const request = dispatch(getInternshipInfoThunk(internshipId));
    const requestPeople = dispatch(
      getInternshipPeopleListThunk({ id: internshipId }),
    );
    return () => {
      request.abort();
      requestPeople.abort();
    };
  }, []);

  const renderContent = useMemo(() => {
    return (
      item && (
        <InternshipInfoBlockComponent info={item} peopleList={peopleList} />
      )
    );
  }, [item, peopleList]);

  const renderOpenInternshipButton = useMemo(() => {
    if (!item) return null;
    const directInternshipPath = `/internship/${item.id}`;

    const handleNavigateToCandidateProfile = () => {
      navigate(directInternshipPath);
      closeModal();
    };

    return (
      location.pathname !== directInternshipPath && (
        <StyledFooterButtonContainer>
          <ButtonComponent
            handleClick={handleNavigateToCandidateProfile}
            appearance="primary"
            align="center"
            className={`open-button`}
          >
            {t('Open internship')}
          </ButtonComponent>
        </StyledFooterButtonContainer>
      )
    );
  }, [item, location]);

  useEffect(() => {
    const handlePopState = () => {
      closeModal();
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <StyledInternshipInfoModal>
      <BaseModalComponent
        header={item?.name ? item.name : undefined}
        footer={renderOpenInternshipButton}
      >
        {renderContent}
      </BaseModalComponent>
    </StyledInternshipInfoModal>
  );
};

export default InternshipInfoModal;
